import { Button, Image, QRCode, Select, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { PayChannelItem, ChainPayProps } from "@/index";
import { usePayChannel } from "@/hooks/usePayChannel";
import { useEffect, useState } from "react";
import clsx from "clsx";
import QrcodeImg from "@/assets/qrcode.png";
import CardImg from "@/assets/card.png";
import KhqrImg from "@/assets/khqr.png";
import Card1Img from "@/assets/card-1.png";
import Card2Img from "@/assets/card-2.png";
import Card3Img from "@/assets/card-3.png";
import Card4Img from "@/assets/card-4.png";
import CopyImg from "@/assets/copy.png";
import CopyGrayImg from "@/assets/copy-gray.png";

const { Paragraph } = Typography;

export type PayChannelProps = {
  value?: string;
  code?: string;
  link?: string;
  iconName?: string;
  countDown?: number;
  currency?: string;
  orderNo?: string;
  getChannelApiUrl?: string;
  paymentComputed?: boolean;
  payState?: string;
};
interface SelectPayChannelProps extends ChainPayProps {
  onPayChannelChange: (data: PayChannelProps) => void;
}

async function createOrder(
  {
    orderParams,
    appId,
    getChannelApiUrl,
  }: {
    orderParams: any;
    appId: string;
    getChannelApiUrl: string;
  },
  isChangeOrder?: boolean
): Promise<string> {
  return await fetch(
    `${getChannelApiUrl}/${isChangeOrder ? "changeOrderChannel" : "createOrder"
    }`,
    {
      method: "POST",
      headers: {
        "app-id": appId,
      },
      body: JSON.stringify(orderParams),
    }
  ).then((res) => res.json());
}

export default function SelectPayChannel({
  appId,
  amount,
  currency,
  testApi = false,
  devApi = false,
  orderId = "",
  countDown = 10 * 60 * 1000,
  onError,
  getSignInfoFn,
  onPayChannelChange = () => { },
}: SelectPayChannelProps) {
  const { t } = useTranslation();

  const [orderNo, setOrderNo] = useState<string>("");

  const getChannelApiUrl = testApi
    ? (process as any)?.env?.testEnv?.VITE_APP_BASE_API
    : import.meta.env.DEV || devApi
      ? (process as any)?.env?.devEnv?.VITE_APP_BASE_API
      : (process as any)?.env?.prodEnv?.VITE_APP_BASE_API;

  const iconAssetsPrefix = testApi
    ? (process as any)?.env?.testEnv?.VITE_APP_ICON_ASSETS_PREFIX
    : import.meta.env.DEV || devApi
      ? (process as any)?.env?.devEnv?.VITE_APP_ICON_ASSETS_PREFIX
      : (process as any)?.env?.prodEnv?.VITE_APP_ICON_ASSETS_PREFIX;

  const landingPageUrl = `${testApi
    ? (process as any)?.env?.testEnv?.VITE_APP_LANDING_PAGE_URL
    : import.meta.env.DEV || devApi
      ? (process as any)?.env?.devEnv?.VITE_APP_LANDING_PAGE_URL
      : (process as any)?.env?.prodEnv?.VITE_APP_LANDING_PAGE_URL
    }?id=${orderNo}`;

  const { loading, payChannel, setPayChannel } = usePayChannel({
    appId,
    getChannelApiUrl,
  });
  const [btnLoading, setBtnLoading] = useState<boolean>(false);

  const [selectPayChannel, setSelectPayChannel] = useState<string>("");
  const [selectPayChannelSub, setSelectPayChannelSub] = useState<string>("");

  // v2.1 Place order immediately upon entry to generate QR code with order number
  useEffect(() => {
    if (orderId) {
      setOrderNo(orderId);
      return;
    }
    try {
      (async () => {
        const orderParams = await getSignInfoFn?.({
          amount: String(amount),
        });
        if (typeof orderParams !== "object") {
          throw new Error("orderParams is not object");
        }

        const res: any = await createOrder({
          orderParams,
          appId,
          getChannelApiUrl,
        } as any);

        setOrderNo(res?.data?.orderNo || "");
      })();
    } catch (error) { }
  }, [orderId]);

  const onPaySubmit = async () => {
    if (btnLoading) return;
    if (!getSignInfoFn && !orderId) return;

    const targetChannel: any = payChannel.find(
      (item: PayChannelItem) => item.name === selectPayChannel
    );
    const targetCurrency: any =
      targetChannel?.currencys?.find?.(
        (item: any) => item.currencyId === targetChannel.currency
      ) ?? {};

    setBtnLoading(true);
    const channel_pay_type =
      selectPayChannelSub === "abapay_khqr"
        ? "abapay_khqr_deeplink"
        : selectPayChannelSub;
    try {
      const orderRes = await fetch(
        `${getChannelApiUrl}/getOrderStatus?order_no=${orderId || orderNo}`,
        {
          method: "GET",
        }
      )
        .then((res) => res.json())
        .then((res) => res.data);

      if (orderRes?.state !== "NON_PAYMENT") {
        setBtnLoading(false);
        onPayChannelChange({
          value: selectPayChannel,
          payState: orderRes?.state,
          orderNo,
          getChannelApiUrl,
        });
        return;
      }

      const orderInfo: any = await createOrder(
        {
          orderParams: {
            payChannel: selectPayChannel,
            chainpayOrderNo: orderId || orderNo,
            currency: targetCurrency.token,
            currencyId: targetCurrency.currencyId,
            extra: {
              channel_pay_type,
            },
          },
          appId,
          getChannelApiUrl,
        } as any,
        true
      );

      const { qrcode, extra, scheme } = orderInfo?.data || {};

      if (extra?.requestUrl) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = extra.requestUrl;
        form.target = "_blank";

        for (const key in extra.data) {
          if (Object.prototype.hasOwnProperty.call(extra.data, key)) {
            const value = extra.data[key];
            if (value) {
              const input = document.createElement("input");
              input.type = "hidden";
              input.name = key;
              input.value = value;
              form.appendChild(input);
            }
          }
        }
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      }

      if (scheme) {
        // 不依赖 iframe.onerror，改用页面可见性/失焦+定时器进行失败回退
        let didLeavePage = false;
        let timer: any;

        const cleanup = () => {
          document.removeEventListener("visibilitychange", onVisibilityChange);
          window.removeEventListener("blur", onVisibilityChange);
          window.removeEventListener("pagehide", onVisibilityChange as any);
          if (timer) clearTimeout(timer);
        };

        const onVisibilityChange = () => {
          // 页面进入隐藏或失焦，视为已跳转到外部 APP
          if (document.hidden) {
            didLeavePage = true;
            cleanup();
          }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("blur", onVisibilityChange);
        window.addEventListener("pagehide", onVisibilityChange as any);

        // 触发外部 APP 打开
        try {
          // 使用 location 跳转，对主流浏览器和 iOS 兼容性更好
          window.location.href = scheme;
        } catch (e) {
          // ignore
        }

        // 在一定时间内未离开页面则回退到二维码/链接方式
        timer = setTimeout(() => {
          if (!didLeavePage) {
            onPayChannelChange({
              value: selectPayChannel,
              code: qrcode,
              link:
                selectPayChannel === "huione"
                  ? ""
                  : selectPayChannelSub === "abapay_khqr_deeplink"
                    ? extra.abapay_deeplink
                    : qrcode,
              countDown:
                selectPayChannelSub === "cards" ? 3 * 60 * 1000 : countDown,
              currency: targetChannel.currency,
              getChannelApiUrl,
              orderNo,
            });
          }
          cleanup();
        }, 1500);

        return;
      }

      // setBtnLoading(false);

      onPayChannelChange({
        value: selectPayChannel,
        code: qrcode,
        link:
          selectPayChannel === "huione"
            ? ""
            : selectPayChannelSub === "abapay_khqr_deeplink"
              ? extra.abapay_deeplink
              : qrcode,
        countDown: selectPayChannelSub === "cards" ? 3 * 60 * 1000 : countDown,
        currency: targetChannel.currency,
        getChannelApiUrl,
        orderNo,
      });
    } catch (error: any) {
      setBtnLoading(false);
      onError?.(error);

    }
  };

  const paywaySubList = [
    { id: "abapay_khqr_deeplink", title: `${t("Open")} Payway APP` },
    {
      id: "abapay_khqr",
      icon: QrcodeImg,
      title: t("Scan QR code"),
      subIcon: {
        width: 43,
        height: 19,
        list: [KhqrImg],
      },
    },
    {
      id: "cards",
      icon: CardImg,
      title: t("Card"),
      subIcon: {
        width: 24,
        height: 24,
        list: [Card1Img, Card2Img, Card3Img, Card4Img],
      },
    },
  ];

  const isChangeChannel = !!orderId;

  return (
    <>
      {!!orderNo && (
        <div className="flex flex-col border-[1px] border-solid border-[#DFDFDF] bg-white rounded-[12px] py-[16px] px-[24px] sm:px-[20px] mt-[15px] sm:pt-[10px]">
          <div className="flex justify-between text-[12px] font-[600] text-[#0D0D0D]">
            <span
              data-font="bold"
              className={clsx("sm:hidden", { hidden: isChangeChannel })}
            >
              {t("需支付总额")}
            </span>
            <div className="flex items-center font-[500] text-[#7F7F7F]">
              {t("Order ID")}：{orderNo}
              <Paragraph
                copyable={{
                  text: orderNo,
                  icon: [
                    <img
                      src={CopyGrayImg}
                      alt=""
                      className="w-[13px] h-[13px]"
                    />,
                  ],
                }}
                className="!mb-0 pl-[7px]"
              />
            </div>
          </div>
          <div
            className={clsx("flex gap-[20px] pt-[20px] sm:pt-[10px]", {
              "!pt-[10px]": isChangeChannel,
            })}
          >
            <QRCode
              errorLevel="H"
              value={landingPageUrl}
              size={100}
              bordered={false}
              className={clsx(
                "min-w-[100px] sm:min-w-[80px] sm:max-h-[80px] mt-[4px]",
                {
                  hidden: isChangeChannel,
                }
              )}
            />
            <div className="flex flex-col w-full">
              <span
                className={clsx("hidden pb-[10px] sm:pb-[5px]", {
                  "text-center !block": isChangeChannel,
                })}
              >
                {t("需支付总额")}
              </span>
              <div
                data-font="bold"
                className={clsx(
                  "flex items-center font-[600] text-[36px] leading-none",
                  { "justify-center": isChangeChannel }
                )}
              >
                {amount}{" "}
                <span className="pl-[10px] pt-[5px] text-[24px]">
                  {currency}
                </span>
              </div>
              <div
                className={clsx("flex items-center pt-[5px] h-max", {
                  hidden: isChangeChannel,
                })}
              >
                <span data-font="bold" className="text-[12px] font-[700]">
                  {t("复制付款链接")}
                </span>
                <Paragraph
                  copyable={{
                    text: landingPageUrl,
                    icon: [
                      <img
                        src={CopyImg}
                        alt=""
                        className="w-[13px] h-[13px]"
                      />,
                    ],
                  }}
                  className="!mb-0 pl-[7px]"
                />
              </div>
              <span
                className={clsx(
                  "pt-[10px] text-[12px] font-[400] text-[#3B3B3B] leading-normal sm:hidden",
                  {
                    hidden: isChangeChannel,
                  }
                )}
              >
                {t("使用应用程序扫描QR码来完成付款，例如Tokenup")}
              </span>
            </div>
          </div>
          <span
            className={clsx(
              "pt-[10px] text-[12px] font-[400] text-[#3B3B3B] leading-normal md:hidden",
              { hidden: isChangeChannel }
            )}
          >
            {t("使用应用程序扫描QR码来完成付款，例如Tokenup")}
          </span>
        </div>
      )}
      <div className="flex flex-col">
        <div className="pt-[8px] text-[18px] sm:text-[16px] font-[400] text-[#7F7F7F] sm:pt-[10px]">
          {t("请选择支付方式")}
        </div>
        <div className="flex flex-col justify-center mt-[8px] min-h-[60px]">
          <Spin spinning={loading && !payChannel.length}>
            {!payChannel?.length && !loading && (
              <div className="text-center text-[14px] text-[#7F7F7F]">
                {t("暂未获取到支付方式")}
              </div>
            )}
            {payChannel?.map?.((item: PayChannelItem) => (
              <div
                key={item.name}
                className={clsx(
                  "flex flex-col px-[24px] sm:px-[10px] py-[12px] sm:py-[8px] gap-[12px] border-[#DFDFDF] border-solid border-[1px] rounded-[12px] mb-[15px] sm:mb-[20px] dark:border-[rgba(255,255,255,0.3)] md:cursor-pointer",
                  {
                    "outline-[#0D0D0D] outline-2 outline border-[transparent]":
                      selectPayChannel === item.name,
                    "md:hover:bg-[#F5F5F5]":
                      !btnLoading && item.name !== "payway",
                  }
                )}
                onClick={() => {
                  if (btnLoading || item.name === "payway") return;
                  setSelectPayChannel(item.name);
                  setSelectPayChannelSub("");
                }}
              >
                <div className="w-full flex items-center justify-between gap-[12px]">
                  <Image
                    src={`${iconAssetsPrefix}${item.iconName}`}
                    preview={false}
                    width={36}
                  />
                  <div
                    data-font="bold"
                    className="mr-auto text-[16px] font-[700]"
                  >
                    {item.channelShowName}
                  </div>
                  <div className="flex flex-col">
                    <span className="opacity-50 text-[12px] sm:hidden">
                      {t("总计支付")}
                    </span>
                    <div className="flex items-center gap-[10px]">
                      <span className="font-bold">{amount}</span>
                      <Select
                        size="small"
                        className="w-[77px]"
                        value={item.currency}
                        options={item.currencys}
                        fieldNames={{ label: "token", value: "currencyId" }}
                        onChange={(value) => {
                          if (btnLoading) return;
                          setPayChannel((pre: any) => {
                            return pre.map((item: PayChannelItem) => {
                              if (item.name === selectPayChannel) {
                                item.currency = value;
                              }
                              return item;
                            });
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
                {item.name === "payway" && (
                  <div className="flex flex-col">
                    {paywaySubList.map((sub) => (
                      <div
                        key={sub.id}
                        className={clsx(
                          "h-[48px] flex items-center gap-[14px] text-[14px] font-[500] text-[#3B3B3B] px-[10px] rounded-[8px]",
                          {
                            "hover:bg-[#F5F5F5]": !btnLoading,
                            "bg-[#F5F5F5]": selectPayChannelSub === sub.id,
                            "md:hidden": sub.id === "abapay_khqr_deeplink",
                          }
                        )}
                        onClick={() => {
                          if (btnLoading) return;
                          setSelectPayChannel(item.name);
                          setSelectPayChannelSub(sub.id);
                        }}
                      >
                        <Image
                          src={
                            sub.icon ?? `${iconAssetsPrefix}${item.iconName}`
                          }
                          preview={false}
                          width={20}
                        />
                        <span>{sub.title}</span>
                        <div className="ml-auto flex items-center gap-[8px]">
                          {sub.subIcon?.list?.map?.((icon) => (
                            <Image
                              key={icon}
                              src={icon}
                              preview={false}
                              width={sub.subIcon?.width}
                              height={sub.subIcon?.height}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Spin>
        </div>
        <Button
          color="default"
          variant="solid"
          className="!mt-[18px] sm:!mt-[5px] h-[60px] sm:h-[44px] rounded-[60px] text-[24px] sm:text-[18px] font-[500]"
          loading={btnLoading}
          disabled={!btnLoading && !selectPayChannel}
          onClick={onPaySubmit}
        >
          {t("支付")}
        </Button>
        <span className="flex items-center justify-center pt-[24px] sm:pt-[10px] text-[14px] font-[500] text-[#ACACAC]">
          Powered by ChainPay
        </span>
      </div>
    </>
  );
}
